import {hasQuotePermission, isQuotePermission} from '../../../utils/quote-access';
import {Component, computed, inject, OnInit, TemplateRef, ViewChild, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from "@angular/router";
import {JobsService} from "../../../services/jobs.service";
import {Permissions} from "../../../enums/permissions";
import {SessionService} from "../../../services/session.service";
import {DateUtils} from "../../../utils/date-utils";
import {TableData} from "../../../interfaces/table/table-data";
import {ToastService} from "../../../services/toast.service";
import {constructAssetsColumns, JobAssetsColumn} from "./job-assets-table.config";
import {KeyValuePairTableData} from "../../../helpers/key-value-pair-table-value-type";
import {TemplateAndValue} from "../../../helpers/template-and-value";
import {PageRedirect} from "../../../helpers/page-redirect";

@Component({
  selector: 'app-job',
  templateUrl: './job.component.html',
  styleUrls: ['./job.component.css'],
  standalone: false
})
export class JobComponent implements OnInit {
  jobNumber: string | null = null;
  jobId: any = null;
  jobDetails: any = null;
  jobDetailsFields: KeyValuePairTableData[] = [];
  activeTab: 'assets' | 'attachments' | 'comments' | 'history' = 'assets';
  currentUser: any;
  isDetailsLoaded: boolean | null = null;
  jobCustomFields: any[] = [];
  jobStatuses: any[] = [];
  jobAttachments: any[] = [];
  jobComments: any[] = [];
  loadingComments: boolean = true;
  historyTableData: TableData = {data: []};
  loadingHistory: boolean = false;

  assetsTableData: TableData = {data: []};
  loadingAssets: boolean = false;
  assetsColumns = computed(() =>
    constructAssetsColumns(this.assetColumn));
  @ViewChild('assetColumn', {static: true}) assetColumn!: TemplateRef<any>;

  @ViewChild('boldField', {static: true}) boldField!: TemplateRef<any>;
  @ViewChild('tagField', {static: true}) tagField!: TemplateRef<any>;
  @ViewChild('locationField', {static: true}) locationField!: TemplateRef<any>;

  protected readonly Permission = Permissions;
  protected readonly JobAssetsColumn = JobAssetsColumn;
  private readonly activeRouter = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobService = inject(JobsService);
  private readonly sessionService = inject(SessionService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser = res.user);
  }

  ngOnInit() {
    this.jobId = this.activeRouter.snapshot.queryParams['job_id'];
    if (!this.jobId) {
      this.returnToJobs();
      return;
    }
    this.getJobDetails();
    this.getJobAssets()
    this.getJobStatuses();
    this.getJobHistories();
    this.getJobAttachments();
    this.getJobComments();
    this.getJobCustomFields();
  }

  getJobAssets() {
    this.loadingAssets = true;
    this.jobService.getJobAssetsById(this.jobId).then((res: any) => {
      this.loadingAssets = false;
      if (!res || !res.Data || res.Data.length == 0) {
        return;
      }
      this.assetsTableData = {
        data: res.Data.map((x: any, index: number) => {
          return {
            Index: index,
            AssetId: x.AssetId,
            Asset: x.Asset == "" || x.Asset == null ? '--' : x.Asset,
            BatterySent: parseInt(x.BatterySent) === 1,
            PowerCordSent: parseInt(x.PowerCordSent) === 1,
            IsOwner: x.IsOwner,
            Warranty: parseInt(x.UnderWarranty) === 1,
            PmPreApproved: parseInt(x.PmPreapproval) === 1,
            PmContract: parseInt(x.PmContract) === 1,
            PoNumber: x.PoNumber == "" || x.PoNumber == null ? '--' : x.PoNumber,
            AssetType: x.AssetType,
            AssetNumber: x.AssetNumber == "" || x.AssetNumber == null ? '--' : x.AssetNumber,
            SerialNumber: x.SerialNumber,
            Accessories: x.Accessories == "" || x.Accessories == null ? '--' : x.Accessories,
            CustomerConcern: x.CustomerConcern == "" || x.CustomerConcern == null ? '--' : x.CustomerConcern,
            CorrectiveActions: x.CorrectiveActions == "" || x.CorrectiveActions == null ? '--' : x.CorrectiveActions,
            DeviceChecks: x.DeviceChecks == "" || x.DeviceChecks == null ? '--' : x.DeviceChecks
          }
        })
      };
    })
  }

  getJobDetails() {
    this.jobService.getJobById(this.jobId).then((res: any) => {
      if (res && res.Data && res.Data.JobId) {
        this.jobDetails = res.Data;
        this.jobNumber = res.Data.JobNumber;
        this.isDetailsLoaded = true;
        this.jobDetailsFields = this.constructJobDetailsFields();
      } else {
        this.isDetailsLoaded = false;
      }
    });
  }

  getJobStatuses() {
    this.jobService.getJobStatuses().then((data: any) => {
      if (data.length > 0) {
        this.jobStatuses = data;
        this.jobDetailsFields = this.constructJobDetailsFields();
      }
    });
  }

  getJobAttachments() {
    this.jobService.getJobAttachments(this.jobId).then((data: any) => {
      if (!data || !Array.isArray(data.Data)) return;
      this.jobAttachments = data.Data;
    });
  }

  getJobComments() {
    this.loadingComments = true;
    this.jobService.getJobComment(this.jobId).then((data: any) => {
      this.loadingComments = false;
      if (!data || !data.Data) {
        return;
      }
      this.jobComments = data.Data.map((x: any) => {
        return {
          comment_id: x.comment_id,
          cp_user_id: x.cp_user_id,
          content: x.content,
          name: x.name,
          email: x.email,
          role_name: x.role,
          date: DateUtils.formatDateTime(x.created_dt)
        }
      });
    });
  }

  getJobHistories() {
    this.loadingHistory = true;
    this.jobService.getJobHistory(this.jobId).then((response: any) => {
      this.loadingHistory = false;
      if (!response || !response.Data) {
        this.toastService.showToast('Failed to load job history', 'error');
        return;
      }
      this.historyTableData = {
        data: response.Data.map((item: any) => ({
          history_id: item.job_history_id,
          action_description: item.action_description,
          user_name: item.first_name + ' ' + item.last_name,
          email: item.user_email,
          role_name: item.role_name,
          date: DateUtils.formatDateTime(item.modified_dt)
        }))
      };
    });
  }

  getJobCustomFields() {
    this.jobService.getJobCustomFields(this.jobId).then((res: any) => {
      if (res && res.length > 0) {
        this.jobCustomFields = res;
        this.jobDetailsFields = this.constructJobDetailsFields();
      }
    })
  }

  changeTab(tab: 'assets' | 'attachments' | 'comments' | 'history') {
    this.activeTab = tab;
  }

  constructJobDetailsFields(): KeyValuePairTableData[] {
    const jobStatus = this.jobStatuses.find((x: any) => x.job_status_name == this.jobDetails?.JobStatus);
    const data: KeyValuePairTableData[] = [
      {
        key: 'Status',
        value: new TemplateAndValue(this.tagField, {
          name: this.jobDetails?.JobStatus,
          color: jobStatus?.color ?? '#D3D3D3'
        }),
        keyAligning: 'center'
      },
      {key: 'Job Type', value: new TemplateAndValue(this.boldField, this.jobDetails?.JobType)},
      {key: 'Due Date', value: new TemplateAndValue(this.boldField, DateUtils.formatDate(this.jobDetails?.DueDate))},
      {key: 'Description', value: new TemplateAndValue(this.boldField, this.plainDescription)},
      {
        key: 'Location',
        value: new TemplateAndValue(this.locationField, {
          name: this.jobDetails?.Location,
          address: this.jobDetails?.LocationFullAddress,
          phone: this.jobDetails?.LocationPhone
        })
      },
      {key: 'Technician', value: new TemplateAndValue(this.boldField, this.jobDetails?.Technician || 'Unassigned')}
    ];

    if (this.currentUser?.is_owner == 1 && this.hasPermission(this.Permission.ViewQuoteDetails)) {
      data.push({
        key: 'Quote Number',
        value: this.jobDetails?.QuoteNumber
          ? new PageRedirect('/quote', this.jobDetails?.QuoteNumber, {quote_id: this.jobDetails?.QuoteNumber})
          : ''
      });
      if (this.jobDetails?.QuoteNumber) {
        data.push({
          key: 'Quote Status',
          value: new TemplateAndValue(this.tagField, {
            name: this.jobDetails?.QuoteStatus,
            color: this.jobDetails?.QuoteStatusColor
          })
        });
      }
    }

    data.push(
      {key: 'Order No.', value: this.jobDetails?.OrderNumber || ''},
      {key: 'Return Tracking No.', value: this.jobDetails?.ReturnTrackingNumber || ''},
      {key: 'Renew Pick Ticket No.', value: this.jobDetails?.RenewPickTicketNumber || ''}
    );

    if (this.currentUser?.is_owner == 1 && this.currentUser?.is_rentee == 0) {
      for (const field of this.jobCustomFields) {
        data.push({
          key: field.display_name,
          value: field?.value || ''
        });
      }
    }

    return data;
  }

  getTotalNumber(value: any) {
    return value.length > 0 ? value[0].total_count : 0;
  }

  get plainDescription(): string {
    const html = this.jobDetails?.JobDescription || '';
    const text = html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text || text === '<p><br></p>') return '--';
    return text;
  }

  returnToJobs() {
    this.router.navigate(['/jobs']);
  }

  hasPermission(permission: string): any {
    if (isQuotePermission(permission)) return hasQuotePermission(this.currentUser, permission);
    if (!this.currentUser) return false;
    const permissions = this.currentUser?.permission.split(',');
    return permissions.includes(permission);
  }

  createComment(commentContent: string) {
    this.loadingComments = true;
    this.jobService.createJobComment(this.jobId, commentContent).then(() => {
      this.getJobComments();
    });
  }

  editComment(data: { commentId: string; commentContent: string }) {
    this.loadingComments = true;
    this.jobService.updateJobComment(this.jobId, data.commentId, data.commentContent).then(() => {
      this.getJobComments();
    });
  }

  deleteComment(commentId: string) {
    this.loadingComments = true;
    this.jobService.deleteJobComment(this.jobId, commentId).then(() => {
      this.getJobComments();
    })
  }
}
